import React, { useEffect, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuIcon from '@mui/icons-material/Menu';
import CircleIcon from '@mui/icons-material/Circle';
import LogoutIcon from '@mui/icons-material/Logout';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import CheckIcon from '@mui/icons-material/Check';
import LanguageIcon from '@mui/icons-material/Language';
import { healthAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useSite } from '../contexts/SiteContext';
import NotificationBell from './NotificationBell';

interface TopBarProps {
  onMenuClick: () => void;
}

const TopBar: React.FC<TopBarProps> = ({ onMenuClick }) => {
  const [dbStatus, setDbStatus] = useState<'ok' | 'error' | 'loading'>('loading');
  const { user, activeOrg, orgs, logout } = useAuth();
  const { sites, activeSiteId, setActiveSiteId, activeSite } = useSite();
  const [siteAnchor, setSiteAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const checkHealth = () => {
      healthAPI.check()
        .then((res) => setDbStatus(res.data.checks.db === 'ok' ? 'ok' : 'error'))
        .catch(() => setDbStatus('error'));
    };
    checkHealth();
    const interval = setInterval(checkHealth, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.primary',
      }}
    >
      <Toolbar sx={{ minHeight: 56 }}>
        <IconButton
          aria-label="open navigation"
          edge="start"
          onClick={onMenuClick}
          sx={{ mr: 2, display: { md: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        {/* Site context switcher */}
        <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1 }}>
          <Button
            onClick={(e) => setSiteAnchor(e.currentTarget)}
            size="small"
            startIcon={<LanguageIcon sx={{ fontSize: 18 }} />}
            endIcon={<UnfoldMoreIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.primary',
              minHeight: 32,
              maxWidth: { xs: 200, sm: 300, md: 'none' },
              justifyContent: 'flex-start',
            }}
            aria-label="Switch site"
          >
            <Typography
              noWrap
              variant="body2"
              sx={{ fontWeight: 600 }}
            >
              {activeSite?.name || 'All Sites'}
            </Typography>
          </Button>
          {orgs.length > 1 && activeOrg && (
            <Typography variant="caption" color="text.secondary" sx={{ pl: 1, lineHeight: 1, display: { xs: 'none', sm: 'block' } }}>
              {activeOrg.name}
            </Typography>
          )}
        </Box>
        <Menu
          anchorEl={siteAnchor}
          open={Boolean(siteAnchor)}
          onClose={() => setSiteAnchor(null)}
          slotProps={{ paper: { sx: { minWidth: 220, maxHeight: 400 } } }}
        >
          <MenuItem
            selected={!activeSiteId}
            onClick={() => { setActiveSiteId(null); setSiteAnchor(null); }}
          >
            <ListItemText>All Sites</ListItemText>
            {!activeSiteId && (
              <ListItemIcon sx={{ minWidth: 'auto', ml: 1 }}>
                <CheckIcon fontSize="small" color="primary" />
              </ListItemIcon>
            )}
          </MenuItem>
          {sites.map((site) => (
            <MenuItem
              key={site.id}
              selected={site.id === activeSiteId}
              onClick={() => { setActiveSiteId(site.id); setSiteAnchor(null); }}
            >
              <ListItemText>{site.name}</ListItemText>
              {site.id === activeSiteId && (
                <ListItemIcon sx={{ minWidth: 'auto', ml: 1 }}>
                  <CheckIcon fontSize="small" color="primary" />
                </ListItemIcon>
              )}
            </MenuItem>
          ))}
        </Menu>

        <Box sx={{ flexGrow: 1 }} />

        <NotificationBell />

        <Chip
          icon={
            <CircleIcon
              sx={{
                fontSize: 10,
                color: dbStatus === 'ok' ? '#10B981' : dbStatus === 'error' ? '#DC2626' : '#94A3B8',
              }}
            />
          }
          label={dbStatus === 'ok' ? 'Connected' : dbStatus === 'error' ? 'Disconnected' : 'Checking...'}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />

        {user && (
          <>
            <Avatar
              sx={{ width: 32, height: 32, ml: 2, bgcolor: 'primary.main', fontSize: 14 }}
            >
              {(user.display_name || user.email)[0].toUpperCase()}
            </Avatar>
            <Typography
              variant="body2"
              sx={{ ml: 1, fontWeight: 500, display: { xs: 'none', sm: 'block' } }}
            >
              {user.display_name || user.email}
            </Typography>
            <Button
              size="small"
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
              sx={{ ml: 1, minHeight: 44 }}
              aria-label="Log out"
            >
              Logout
            </Button>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
